import React from 'react';

import { Accordion } from '@/components/Accordion';
import { AnimatedMicroMarketDescription } from '@/components/AnimatedMicroMarketDescription';
import { SgpIcon } from '@/components/SgpIcon';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { useIsMarketPublished, useMarketStatus } from '@/feature/event-details-sbk/hooks/use-market-cache';
import { useMicroMarket } from '@/feature/event-details-sbk/hooks/use-micro-market-cache';
import { Event } from '@/feature/event-details-sbk/types';
import { useIsSgpEnabled } from '@/hooks/use-is-sgp-enabled';

import { MarketName } from '../../MarketName';
import { MarketLayouts } from '../../market-layouts';
import { styles } from '../market-accordion-layout/MarketAccordion';

type MicroMarketAccordionProps = {
    microMarketId: string;
    event: Event;
    isExpanded?: boolean;
    testID?: string;
};

export const MicroMarketAccordion = ({ microMarketId, event, isExpanded, testID }: MicroMarketAccordionProps) => {
    const market = useMicroMarket(microMarketId);
    const isMarketPublished = useIsMarketPublished(market);
    const marketStatus = useMarketStatus(market.id);
    const showDescription = marketStatus !== 'SUSPENDED' && isMarketPublished;
    const isSgpEnabled = useIsSgpEnabled(event.is_sgp_enabled);

    if (!market) {
        return null;
    }

    return (
        <Accordion
            header={
                <Box justifyContent="space-between" flexDirection="row" alignItems="center">
                    <Box flexShrink={1}>
                        <MarketName
                            sportName={event.sport.name}
                            event={event}
                            fallback={market.description}
                            marketType={market.market_type}
                            testID={`marketName-${testID}`}
                            color={ds.colorToken.text.primary}
                            numberOfLines={1}
                        />
                        <AnimatedMicroMarketDescription
                            event={event}
                            marketType={market.market_type}
                            showDescription={showDescription}
                        />
                    </Box>
                    {isSgpEnabled ? (
                        <Box mr="s12" ml="s12">
                            <SgpIcon />
                        </Box>
                    ) : null}
                </Box>
            }
            content={<MarketLayouts {...{ event, markets: [market], layout: market.layout }} />}
            initialExpanded={isExpanded}
            accordionStyle={styles.accordionStyle}
        />
    );
};
